import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  Clock,
  Plus,
  RefreshCw,
  AlertTriangle,
  CheckCircle2,
  CalendarClock,
  Timer,
  Search,
  Filter,
  ChevronRight,
  XCircle,
  ArrowRight,
  Shield,
  Zap,
  X,
  Settings,
} from "lucide-react";
import { ipDocketApi } from "../../services/api";
import PremiumModal from "../ui/PremiumModal";
import IPRulesSettings from "./IPRulesSettings";
import { getJurisdiction } from "./jurisdictionConfig";

// ─── Types ──────────────────────────────────────────────────────────────────

interface DocketEntry {
  _id: string;
  matterId: string | { _id: string; title: string; fileNumber?: string };
  clientId?: string | { _id: string; displayName?: string; firstName?: string; lastName?: string };
  title: string;
  description?: string;
  dueDate: string;
  gracePeriodDate?: string;
  status: "pending" | "completed" | "waived" | "overdue";
  priority: "critical" | "high" | "medium" | "low";
  jurisdiction?: string;
  ipType?: "patent" | "trademark" | "copyright" | "industrial_design" | "trade_secret";
  assignedTo?: string | { _id: string; name: string };
  completedAt?: string;
  completionNotes?: string;
  waivedReason?: string;
  source?: "computed" | "manual";
}

interface DocketSummary {
  overdue: number;
  dueThisWeek: number;
  dueThisMonth: number;
  due90Days: number;
}

// ─── Constants ──────────────────────────────────────────────────────────────

const PRIORITY_STYLES: Record<string, { bg: string; text: string; dot: string }> = {
  critical: { bg: "bg-red-50", text: "text-red-700", dot: "bg-red-500" },
  high: { bg: "bg-amber-50", text: "text-amber-700", dot: "bg-amber-500" },
  medium: { bg: "bg-blue-50", text: "text-blue-700", dot: "bg-blue-500" },
  low: { bg: "bg-slate-50", text: "text-slate-600", dot: "bg-slate-400" },
};

const STATUS_FILTERS = ["all", "overdue", "due_soon", "upcoming", "completed"] as const;
const JURISDICTION_FILTERS = ["all", "CA", "US", "PCT", "EP"] as const;
const IP_TYPE_FILTERS = ["all", "patent", "trademark"] as const;
const PRIORITY_FILTERS = ["all", "critical", "high", "medium", "low"] as const;

// ─── Helpers ────────────────────────────────────────────────────────────────

const formatDate = (d: string) =>
  new Date(d).toLocaleDateString("en-CA", { year: "numeric", month: "long", day: "numeric" });

const getTimeRemaining = (dueDate: string): { text: string; color: string; isOverdue: boolean } => {
  const now = new Date();
  const due = new Date(dueDate);
  const diffMs = due.getTime() - now.getTime();
  const diffDays = Math.ceil(diffMs / 86400000);

  if (diffDays < 0) {
    const absDays = Math.abs(diffDays);
    if (absDays === 1) return { text: "1 day overdue", color: "text-red-600", isOverdue: true };
    if (absDays < 30) return { text: `${absDays} days overdue`, color: "text-red-600", isOverdue: true };
    const months = Math.floor(absDays / 30);
    return { text: `${months} month${months > 1 ? "s" : ""} overdue`, color: "text-red-600", isOverdue: true };
  }
  if (diffDays === 0) return { text: "Due today", color: "text-red-600", isOverdue: false };
  if (diffDays === 1) return { text: "Due tomorrow", color: "text-amber-600", isOverdue: false };
  if (diffDays <= 7) return { text: `${diffDays} days left`, color: "text-amber-600", isOverdue: false };
  if (diffDays <= 30) return { text: `${diffDays} days left`, color: "text-yellow-600", isOverdue: false };
  const months = Math.floor(diffDays / 30);
  return { text: `${months} month${months > 1 ? "s" : ""} left`, color: "text-emerald-600", isOverdue: false };
};

const getDateGroup = (dueDate: string): string => {
  const now = new Date();
  const due = new Date(dueDate);
  const diffDays = Math.ceil((due.getTime() - now.getTime()) / 86400000);

  if (diffDays < 0) return "Overdue";
  if (diffDays === 0) return "Today";
  if (diffDays <= 7) return "This Week";
  if (diffDays <= 30) return "This Month";
  return "Later";
};

const getStatusBorder = (dueDate: string, status: string): string => {
  if (status === "completed" || status === "waived") return "border-l-slate-300";
  const diffDays = Math.ceil((new Date(dueDate).getTime() - Date.now()) / 86400000);
  if (diffDays < 0) return "border-l-red-500";
  if (diffDays <= 7) return "border-l-amber-500";
  if (diffDays <= 30) return "border-l-yellow-400";
  return "border-l-emerald-500";
};

const getStatusBadge = (dueDate: string, status: string) => {
  if (status === "completed") return { label: "COMPLETED", bg: "bg-slate-100", text: "text-slate-600" };
  if (status === "waived") return { label: "WAIVED", bg: "bg-slate-100", text: "text-slate-500" };
  const diffDays = Math.ceil((new Date(dueDate).getTime() - Date.now()) / 86400000);
  if (diffDays < 0) return { label: "OVERDUE", bg: "bg-red-100", text: "text-red-700" };
  if (diffDays <= 7) return { label: "DUE SOON", bg: "bg-amber-100", text: "text-amber-700" };
  if (diffDays <= 30) return { label: "THIS MONTH", bg: "bg-yellow-100", text: "text-yellow-700" };
  return { label: "UPCOMING", bg: "bg-emerald-100", text: "text-emerald-700" };
};

const getMatterInfo = (entry: DocketEntry) => {
  const m = entry.matterId;
  if (typeof m === "object" && m) {
    return { id: m._id, title: m.title, fileNumber: m.fileNumber };
  }
  return { id: m as string, title: "", fileNumber: "" };
};

const getClientName = (entry: DocketEntry) => {
  const c = entry.clientId;
  if (typeof c === "object" && c) {
    return c.displayName || `${c.firstName || ""} ${c.lastName || ""}`.trim();
  }
  return "";
};

const getAssigneeName = (entry: DocketEntry) => {
  const a = entry.assignedTo;
  if (typeof a === "object" && a) return a.name;
  return "";
};

const formatIpType = (t?: string) => {
  if (!t) return "";
  return t.split("_").map((w) => w.charAt(0).toUpperCase() + w.slice(1)).join(" ");
};

// ─── Component ──────────────────────────────────────────────────────────────

const IPDocketPage: React.FC = () => {
  const navigate = useNavigate();
  const [entries, setEntries] = useState<DocketEntry[]>([]);
  const [summary, setSummary] = useState<DocketSummary>({ overdue: 0, dueThisWeek: 0, dueThisMonth: 0, due90Days: 0 });
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState<string>("all");
  const [jurisdictionFilter, setJurisdictionFilter] = useState<string>("all");
  const [ipTypeFilter, setIpTypeFilter] = useState<string>("all");
  const [priorityFilter, setPriorityFilter] = useState<string>("all");

  // Modal states
  const [completeModal, setCompleteModal] = useState<DocketEntry | null>(null);
  const [extendModal, setExtendModal] = useState<DocketEntry | null>(null);
  const [waiveModal, setWaiveModal] = useState<DocketEntry | null>(null);
  const [addModal, setAddModal] = useState(false);
  const [rulesSettingsOpen, setRulesSettingsOpen] = useState(false);

  // Form states
  const [completionNotes, setCompletionNotes] = useState("");
  const [extendDate, setExtendDate] = useState("");
  const [extendReason, setExtendReason] = useState("");
  const [waiveReason, setWaiveReason] = useState("");
  const [saving, setSaving] = useState(false);

  // Add manual deadline form
  const [newDeadline, setNewDeadline] = useState({
    title: "",
    description: "",
    dueDate: "",
    priority: "medium",
    jurisdiction: "",
    ipType: "",
    matterId: "",
  });

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [entriesRes, summaryRes] = await Promise.all([
        ipDocketApi.list(),
        ipDocketApi.summary(),
      ]);
      setEntries(entriesRes.data?.entries || entriesRes.data || []);
      setSummary(summaryRes.data || { overdue: 0, dueThisWeek: 0, dueThisMonth: 0, due90Days: 0 });
    } catch (err) {
      console.error("Failed to fetch docket data:", err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // ─── Filter logic ─────────────────────────────────────────────────────────

  const filtered = entries.filter((e) => {
    if (search) {
      const q = search.toLowerCase();
      const matterTitle = typeof e.matterId === "object" ? e.matterId.title : "";
      if (
        !e.title.toLowerCase().includes(q) &&
        !matterTitle.toLowerCase().includes(q) &&
        !getClientName(e).toLowerCase().includes(q)
      )
        return false;
    }
    if (statusFilter !== "all") {
      const diffDays = Math.ceil((new Date(e.dueDate).getTime() - Date.now()) / 86400000);
      if (statusFilter === "overdue" && (diffDays >= 0 || e.status === "completed" || e.status === "waived")) return false;
      if (statusFilter === "due_soon" && (diffDays < 0 || diffDays > 7 || e.status === "completed" || e.status === "waived")) return false;
      if (statusFilter === "upcoming" && (diffDays <= 7 || e.status === "completed" || e.status === "waived")) return false;
      if (statusFilter === "completed" && e.status !== "completed" && e.status !== "waived") return false;
    }
    if (jurisdictionFilter !== "all" && e.jurisdiction !== jurisdictionFilter) return false;
    if (ipTypeFilter !== "all" && e.ipType !== ipTypeFilter) return false;
    if (priorityFilter !== "all" && e.priority !== priorityFilter) return false;
    return true;
  });

  // Group by date
  const groups: Record<string, DocketEntry[]> = {};
  const groupOrder = ["Overdue", "Today", "This Week", "This Month", "Later"];
  filtered.forEach((e) => {
    const group = e.status === "completed" || e.status === "waived" ? "Completed" : getDateGroup(e.dueDate);
    if (!groups[group]) groups[group] = [];
    groups[group].push(e);
  });
  // Sort each group by date
  Object.values(groups).forEach((arr) => arr.sort((a, b) => new Date(a.dueDate).getTime() - new Date(b.dueDate).getTime()));
  const orderedGroups = [...groupOrder, "Completed"].filter((g) => groups[g]?.length);

  // ─── Actions ──────────────────────────────────────────────────────────────

  const handleComplete = async () => {
    if (!completeModal) return;
    setSaving(true);
    try {
      await ipDocketApi.complete(completeModal._id, { completionNotes });
      setCompleteModal(null);
      setCompletionNotes("");
      fetchData();
    } catch (err) {
      console.error("Failed to complete:", err);
    } finally {
      setSaving(false);
    }
  };

  const handleExtend = async () => {
    if (!extendModal || !extendDate) return;
    setSaving(true);
    try {
      await ipDocketApi.extend(extendModal._id, { newDueDate: extendDate, reason: extendReason });
      setExtendModal(null);
      setExtendDate("");
      setExtendReason("");
      fetchData();
    } catch (err) {
      console.error("Failed to extend:", err);
    } finally {
      setSaving(false);
    }
  };

  const handleWaive = async () => {
    if (!waiveModal || !waiveReason.trim()) return;
    setSaving(true);
    try {
      await ipDocketApi.waive(waiveModal._id, { reason: waiveReason });
      setWaiveModal(null);
      setWaiveReason("");
      fetchData();
    } catch (err) {
      console.error("Failed to waive:", err);
    } finally {
      setSaving(false);
    }
  };

  const handleAddDeadline = async () => {
    if (!newDeadline.title || !newDeadline.dueDate) return;
    setSaving(true);
    try {
      await ipDocketApi.createManual(newDeadline);
      setAddModal(false);
      setNewDeadline({ title: "", description: "", dueDate: "", priority: "medium", jurisdiction: "", ipType: "", matterId: "" });
      fetchData();
    } catch (err) {
      console.error("Failed to add deadline:", err);
    } finally {
      setSaving(false);
    }
  };

  // ─── Animated counter ─────────────────────────────────────────────────────

  const AnimatedCount: React.FC<{ value: number; color: string }> = ({ value, color }) => (
    <motion.span
      key={value}
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      className={`text-3xl font-bold ${color}`}
    >
      {value}
    </motion.span>
  );

  // ─── Render ───────────────────────────────────────────────────────────────

  return (
    <div className="min-h-screen bg-[#F4F6F8]">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-3">
              <div className="h-10 w-10 rounded-xl bg-[#0F5132] flex items-center justify-center">
                <Clock className="h-5 w-5 text-white" />
              </div>
              IP Docket
            </h1>
            <p className="text-sm text-slate-500 mt-1">
              Track all intellectual property deadlines across your firm
            </p>
          </div>
          <div className="flex items-center gap-3">
            <button
              onClick={() => setRulesSettingsOpen(true)}
              className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-semibold text-slate-700 bg-white border border-slate-200 hover:bg-slate-50 transition-colors shadow-sm"
            >
              <Settings className="h-4 w-4" />
              Rules
            </button>
            <button
              onClick={() => setAddModal(true)}
              className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-bold text-white transition-colors"
              style={{ backgroundColor: "#0F5132" }}
              onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = "#146C43")}
              onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = "#0F5132")}
            >
              <Plus className="h-4 w-4" />
              Add Deadline
            </button>
          </div>
        </div>

        {/* Summary Cards */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0 }}
            className="bg-white rounded-xl shadow-sm border border-slate-200 p-5"
          >
            <div className="flex items-center justify-between mb-3">
              <span className="text-sm font-medium text-slate-500">Overdue</span>
              <div className="h-8 w-8 rounded-lg bg-red-100 flex items-center justify-center">
                <AlertTriangle className="h-4 w-4 text-red-600" />
              </div>
            </div>
            <AnimatedCount value={summary.overdue} color="text-red-600" />
            <p className="text-xs text-slate-400 mt-1">Require immediate attention</p>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.05 }}
            className="bg-white rounded-xl shadow-sm border border-slate-200 p-5"
          >
            <div className="flex items-center justify-between mb-3">
              <span className="text-sm font-medium text-slate-500">Due This Week</span>
              <div className="h-8 w-8 rounded-lg bg-amber-100 flex items-center justify-center">
                <Timer className="h-4 w-4 text-amber-600" />
              </div>
            </div>
            <AnimatedCount value={summary.dueThisWeek} color="text-amber-600" />
            <p className="text-xs text-slate-400 mt-1">Next 7 days</p>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="bg-white rounded-xl shadow-sm border border-slate-200 p-5"
          >
            <div className="flex items-center justify-between mb-3">
              <span className="text-sm font-medium text-slate-500">Due This Month</span>
              <div className="h-8 w-8 rounded-lg bg-yellow-100 flex items-center justify-center">
                <CalendarClock className="h-4 w-4 text-yellow-600" />
              </div>
            </div>
            <AnimatedCount value={summary.dueThisMonth} color="text-yellow-600" />
            <p className="text-xs text-slate-400 mt-1">Next 30 days</p>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.15 }}
            className="bg-white rounded-xl shadow-sm border border-slate-200 p-5"
          >
            <div className="flex items-center justify-between mb-3">
              <span className="text-sm font-medium text-slate-500">Due in 90 Days</span>
              <div className="h-8 w-8 rounded-lg bg-emerald-100 flex items-center justify-center">
                <Shield className="h-4 w-4 text-emerald-600" />
              </div>
            </div>
            <AnimatedCount value={summary.due90Days} color="text-emerald-600" />
            <p className="text-xs text-slate-400 mt-1">Upcoming deadlines</p>
          </motion.div>
        </div>

        {/* Filter Bar */}
        <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-4 mb-6">
          <div className="flex flex-wrap items-center gap-4">
            {/* Search */}
            <div className="relative flex-1 min-w-[220px]">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
              <input
                type="text"
                placeholder="Search deadlines, matters, clients..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="w-full pl-10 pr-4 py-2.5 rounded-xl border border-slate-200 text-sm focus:outline-none focus:border-emerald-600 transition-colors"
              />
            </div>

            {/* Status Pills */}
            <div className="flex items-center gap-1">
              {STATUS_FILTERS.map((s) => (
                <button
                  key={s}
                  onClick={() => setStatusFilter(s)}
                  className={`px-3 py-1.5 rounded-lg text-xs font-semibold transition-colors ${
                    statusFilter === s
                      ? "bg-[#0F5132] text-white"
                      : "bg-slate-100 text-slate-600 hover:bg-slate-200"
                  }`}
                >
                  {s === "all" ? "All" : s === "due_soon" ? "Due Soon" : s.charAt(0).toUpperCase() + s.slice(1)}
                </button>
              ))}
            </div>

            {/* Jurisdiction Pills */}
            <div className="flex items-center gap-1">
              {JURISDICTION_FILTERS.map((j) => (
                <button
                  key={j}
                  onClick={() => setJurisdictionFilter(j)}
                  className={`px-3 py-1.5 rounded-lg text-xs font-semibold transition-colors ${
                    jurisdictionFilter === j
                      ? "bg-[#0F5132] text-white"
                      : "bg-slate-100 text-slate-600 hover:bg-slate-200"
                  }`}
                >
                  {j === "all" ? "All" : (
                    <span className="inline-flex items-center gap-1">
                      <i className={getJurisdiction(j).icon} style={{ color: jurisdictionFilter === j ? "white" : getJurisdiction(j).iconColor, fontSize: "10px" }} />
                      {j}
                    </span>
                  )}
                </button>
              ))}
            </div>

            {/* Priority Pills */}
            <div className="flex items-center gap-1">
              {PRIORITY_FILTERS.map((p) => (
                <button
                  key={p}
                  onClick={() => setPriorityFilter(p)}
                  className={`px-3 py-1.5 rounded-lg text-xs font-semibold transition-colors ${
                    priorityFilter === p
                      ? "bg-[#0F5132] text-white"
                      : "bg-slate-100 text-slate-600 hover:bg-slate-200"
                  }`}
                >
                  {p === "all" ? "All" : p.charAt(0).toUpperCase() + p.slice(1)}
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* Docket List */}
        {loading ? (
          <div className="flex items-center justify-center py-24">
            <div
              className="animate-spin h-8 w-8 rounded-full border-4"
              style={{ borderColor: "#0F5132", borderTopColor: "transparent" }}
            />
          </div>
        ) : filtered.length === 0 ? (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-white rounded-xl shadow-sm border border-slate-200 p-16 text-center"
          >
            <div className="mx-auto h-16 w-16 rounded-2xl bg-emerald-50 flex items-center justify-center mb-4">
              <CheckCircle2 className="h-8 w-8 text-emerald-500" />
            </div>
            <h3 className="text-lg font-semibold text-slate-900 mb-2">
              {entries.length === 0 ? "No IP Cases Yet" : "No Matching Deadlines"}
            </h3>
            <p className="text-sm text-slate-500 max-w-md mx-auto">
              {entries.length === 0
                ? "Create a matter with Intellectual Property as the practice area to get started. Deadlines will be computed automatically based on jurisdiction rules."
                : "Try adjusting your filters to see more results."}
            </p>
            {entries.length === 0 && (
              <button
                onClick={() => navigate("/matters")}
                className="mt-6 inline-flex items-center gap-2 px-5 py-2.5 rounded-xl text-sm font-bold text-white transition-colors"
                style={{ backgroundColor: "#0F5132" }}
                onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = "#146C43")}
                onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = "#0F5132")}
              >
                Go to Matters
                <ArrowRight className="h-4 w-4" />
              </button>
            )}
          </motion.div>
        ) : (
          <div className="space-y-6">
            {orderedGroups.map((groupName) => (
              <div key={groupName}>
                {/* Sticky group header */}
                <div className="sticky top-0 z-10 bg-[#F4F6F8] py-2">
                  <div className="flex items-center gap-3">
                    <h2 className="text-sm font-bold text-slate-700 uppercase tracking-wider">
                      {groupName}
                    </h2>
                    <span className="text-xs font-medium text-slate-400 bg-slate-200 px-2 py-0.5 rounded-full">
                      {groups[groupName].length}
                    </span>
                    <div className="flex-1 h-px bg-slate-200" />
                  </div>
                </div>

                <AnimatePresence>
                  <div className="space-y-3 mt-2">
                    {groups[groupName].map((entry, i) => {
                      const matter = getMatterInfo(entry);
                      const client = getClientName(entry);
                      const assignee = getAssigneeName(entry);
                      const timeRemaining = getTimeRemaining(entry.dueDate);
                      const badge = getStatusBadge(entry.dueDate, entry.status);
                      const borderColor = getStatusBorder(entry.dueDate, entry.status);
                      const priorityStyle = PRIORITY_STYLES[entry.priority] || PRIORITY_STYLES.medium;
                      const isCompleted = entry.status === "completed" || entry.status === "waived";

                      return (
                        <motion.div
                          key={entry._id}
                          initial={{ opacity: 0, x: -20 }}
                          animate={{ opacity: 1, x: 0 }}
                          exit={{ opacity: 0, x: 20, scale: 0.95 }}
                          transition={{ delay: i * 0.03 }}
                          className={`bg-white rounded-xl shadow-sm border border-slate-200 border-l-4 ${borderColor} overflow-hidden transition-shadow hover:shadow-md ${
                            isCompleted ? "opacity-60" : ""
                          }`}
                        >
                          <div className="p-5">
                            {/* Top row: badge + priority */}
                            <div className="flex items-center justify-between mb-3">
                              <span
                                className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11px] font-bold uppercase tracking-wide ${badge.bg} ${badge.text}`}
                              >
                                {badge.label}
                              </span>
                              <span
                                className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11px] font-bold ${priorityStyle.bg} ${priorityStyle.text}`}
                              >
                                <span className={`h-1.5 w-1.5 rounded-full ${priorityStyle.dot}`} />
                                {entry.priority.charAt(0).toUpperCase() + entry.priority.slice(1)} Priority
                              </span>
                            </div>

                            {/* Title */}
                            <h3 className="text-base font-bold text-slate-900 mb-1">
                              {entry.title}
                            </h3>

                            {/* Matter reference */}
                            {matter.title && (
                              <p className="text-sm text-slate-500 mb-2">
                                {matter.fileNumber && (
                                  <span className="font-mono text-xs text-slate-400 mr-2">
                                    {matter.fileNumber}
                                  </span>
                                )}
                                {matter.title}
                              </p>
                            )}

                            {/* Meta row */}
                            <div className="flex flex-wrap items-center gap-x-4 gap-y-1.5 text-sm text-slate-500 mb-4">
                              {client && (
                                <span className="flex items-center gap-1">
                                  Client: <span className="font-medium text-slate-700">{client}</span>
                                </span>
                              )}
                              {entry.jurisdiction && (() => {
                                const j = getJurisdiction(entry.jurisdiction);
                                return (
                                  <span className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-md text-xs font-medium" style={{ background: j.gradient }}>
                                    <i className={j.icon} style={{ color: j.iconColor, fontSize: "10px" }} />
                                    {j.code}
                                  </span>
                                );
                              })()}
                              {entry.ipType && (
                                <span className="flex items-center gap-1">
                                  <span className="font-medium text-slate-700">{formatIpType(entry.ipType)}</span>
                                </span>
                              )}
                            </div>

                            {/* Due date + grace period */}
                            <div className="flex flex-wrap items-center gap-x-6 gap-y-2 mb-4">
                              <div>
                                <span className="text-xs text-slate-400 uppercase tracking-wider block mb-0.5">
                                  Due
                                </span>
                                <span className="text-sm font-semibold text-slate-900">
                                  {formatDate(entry.dueDate)}
                                </span>
                                <span className={`ml-2 text-sm font-medium ${timeRemaining.color}`}>
                                  ({timeRemaining.text})
                                </span>
                              </div>
                              {entry.gracePeriodDate && (
                                <div>
                                  <span className="text-xs text-slate-400 uppercase tracking-wider block mb-0.5">
                                    Grace Period
                                  </span>
                                  <span className="text-sm text-slate-600">
                                    {formatDate(entry.gracePeriodDate)}
                                    <span className="text-slate-400 text-xs ml-1">(with surcharge)</span>
                                  </span>
                                </div>
                              )}
                            </div>

                            {/* Assignee */}
                            {assignee && (
                              <p className="text-sm text-slate-500 mb-4">
                                Assigned to: <span className="font-medium text-slate-700">{assignee}</span>
                              </p>
                            )}

                            {/* Actions */}
                            {!isCompleted && (
                              <div className="flex flex-wrap items-center gap-2 pt-3 border-t border-slate-100">
                                <button
                                  onClick={() => {
                                    setCompleteModal(entry);
                                    setCompletionNotes("");
                                  }}
                                  className="inline-flex items-center gap-1.5 px-3.5 py-2 rounded-lg text-xs font-bold bg-emerald-50 text-emerald-700 hover:bg-emerald-100 transition-colors"
                                >
                                  <CheckCircle2 className="h-3.5 w-3.5" />
                                  Complete
                                </button>
                                <button
                                  onClick={() => {
                                    setExtendModal(entry);
                                    setExtendDate("");
                                    setExtendReason("");
                                  }}
                                  className="inline-flex items-center gap-1.5 px-3.5 py-2 rounded-lg text-xs font-bold bg-amber-50 text-amber-700 hover:bg-amber-100 transition-colors"
                                >
                                  <CalendarClock className="h-3.5 w-3.5" />
                                  Extend
                                </button>
                                <button
                                  onClick={() => {
                                    setWaiveModal(entry);
                                    setWaiveReason("");
                                  }}
                                  className="inline-flex items-center gap-1.5 px-3.5 py-2 rounded-lg text-xs font-bold bg-red-50 text-red-700 hover:bg-red-100 transition-colors"
                                >
                                  <XCircle className="h-3.5 w-3.5" />
                                  Waive
                                </button>
                                {matter.id && (
                                  <button
                                    onClick={() => navigate(`/matters/${matter.id}`)}
                                    className="inline-flex items-center gap-1.5 px-3.5 py-2 rounded-lg text-xs font-bold bg-slate-100 text-slate-700 hover:bg-slate-200 transition-colors ml-auto"
                                  >
                                    View Case
                                    <ChevronRight className="h-3.5 w-3.5" />
                                  </button>
                                )}
                              </div>
                            )}
                          </div>
                        </motion.div>
                      );
                    })}
                  </div>
                </AnimatePresence>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* ─── Complete Modal ────────────────────────────────────────────────── */}
      <PremiumModal
        open={!!completeModal}
        onClose={() => setCompleteModal(null)}
        title="Complete Deadline"
        subtitle={completeModal?.title}
        icon={CheckCircle2}
        size="md"
        footer={
          <div className="flex justify-end gap-3">
            <button
              onClick={() => setCompleteModal(null)}
              className="px-4 py-2.5 rounded-xl text-sm font-medium text-slate-600 hover:bg-slate-100 transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={handleComplete}
              disabled={saving}
              className="px-5 py-2.5 rounded-xl text-sm font-bold text-white transition-colors disabled:opacity-50"
              style={{ backgroundColor: "#0F5132" }}
              onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = "#146C43")}
              onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = "#0F5132")}
            >
              {saving ? "Saving..." : "Mark as Complete"}
            </button>
          </div>
        }
      >
        <div className="space-y-4">
          <p className="text-sm text-slate-600">
            This will mark the deadline as completed. Add optional notes about how it was fulfilled.
          </p>
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1.5">
              Completion Notes (optional)
            </label>
            <textarea
              value={completionNotes}
              onChange={(e) => setCompletionNotes(e.target.value)}
              placeholder="e.g., Non-provisional application filed, serial no. 17/123,456"
              rows={4}
              className="w-full rounded-xl border border-slate-200 px-4 py-3 text-sm focus:outline-none focus:border-emerald-600 transition-colors resize-none"
            />
          </div>
        </div>
      </PremiumModal>

      {/* ─── Extend Modal ─────────────────────────────────────────────────── */}
      <PremiumModal
        open={!!extendModal}
        onClose={() => setExtendModal(null)}
        title="Extend Deadline"
        subtitle={extendModal?.title}
        icon={CalendarClock}
        size="md"
        footer={
          <div className="flex justify-end gap-3">
            <button
              onClick={() => setExtendModal(null)}
              className="px-4 py-2.5 rounded-xl text-sm font-medium text-slate-600 hover:bg-slate-100 transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={handleExtend}
              disabled={saving || !extendDate}
              className="px-5 py-2.5 rounded-xl text-sm font-bold text-white transition-colors disabled:opacity-50"
              style={{ backgroundColor: "#0F5132" }}
              onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = "#146C43")}
              onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = "#0F5132")}
            >
              {saving ? "Saving..." : "Extend Deadline"}
            </button>
          </div>
        }
      >
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1.5">
              New Due Date
            </label>
            <input
              type="date"
              value={extendDate}
              onChange={(e) => setExtendDate(e.target.value)}
              className="w-full rounded-xl border border-slate-200 px-4 py-3 text-sm focus:outline-none focus:border-emerald-600 transition-colors"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1.5">
              Reason for Extension
            </label>
            <textarea
              value={extendReason}
              onChange={(e) => setExtendReason(e.target.value)}
              placeholder="e.g., Client requested additional time for prior art search"
              rows={3}
              className="w-full rounded-xl border border-slate-200 px-4 py-3 text-sm focus:outline-none focus:border-emerald-600 transition-colors resize-none"
            />
          </div>
        </div>
      </PremiumModal>

      {/* ─── Waive Modal ──────────────────────────────────────────────────── */}
      <PremiumModal
        open={!!waiveModal}
        onClose={() => setWaiveModal(null)}
        title="Waive Deadline"
        subtitle={waiveModal?.title}
        icon={XCircle}
        size="md"
        footer={
          <div className="flex justify-end gap-3">
            <button
              onClick={() => setWaiveModal(null)}
              className="px-4 py-2.5 rounded-xl text-sm font-medium text-slate-600 hover:bg-slate-100 transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={handleWaive}
              disabled={saving || !waiveReason.trim()}
              className="px-5 py-2.5 rounded-xl text-sm font-bold text-white bg-red-600 hover:bg-red-700 transition-colors disabled:opacity-50"
            >
              {saving ? "Saving..." : "Waive Deadline"}
            </button>
          </div>
        }
      >
        <div className="space-y-4">
          <div className="bg-red-50 border border-red-200 rounded-xl p-4">
            <p className="text-sm text-red-700 font-medium">
              Warning: Waiving a deadline is irreversible. Ensure this action is intentional and properly documented.
            </p>
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1.5">
              Reason for Waiver <span className="text-red-500">*</span>
            </label>
            <textarea
              value={waiveReason}
              onChange={(e) => setWaiveReason(e.target.value)}
              placeholder="e.g., Client has decided not to pursue national phase entry in this jurisdiction"
              rows={4}
              className="w-full rounded-xl border border-slate-200 px-4 py-3 text-sm focus:outline-none focus:border-emerald-600 transition-colors resize-none"
            />
          </div>
        </div>
      </PremiumModal>

      {/* ─── Add Deadline Modal ───────────────────────────────────────────── */}
      <PremiumModal
        open={addModal}
        onClose={() => setAddModal(false)}
        title="Add Manual Deadline"
        subtitle="Create a custom IP deadline"
        icon={Plus}
        size="lg"
        footer={
          <div className="flex justify-end gap-3">
            <button
              onClick={() => setAddModal(false)}
              className="px-4 py-2.5 rounded-xl text-sm font-medium text-slate-600 hover:bg-slate-100 transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={handleAddDeadline}
              disabled={saving || !newDeadline.title || !newDeadline.dueDate}
              className="px-5 py-2.5 rounded-xl text-sm font-bold text-white transition-colors disabled:opacity-50"
              style={{ backgroundColor: "#0F5132" }}
              onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = "#146C43")}
              onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = "#0F5132")}
            >
              {saving ? "Saving..." : "Create Deadline"}
            </button>
          </div>
        }
      >
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1.5">
              Deadline Title <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              value={newDeadline.title}
              onChange={(e) => setNewDeadline((p) => ({ ...p, title: e.target.value }))}
              placeholder="e.g., Response to Office Action"
              className="w-full rounded-xl border border-slate-200 px-4 py-3 text-sm focus:outline-none focus:border-emerald-600 transition-colors"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1.5">
              Description
            </label>
            <textarea
              value={newDeadline.description}
              onChange={(e) => setNewDeadline((p) => ({ ...p, description: e.target.value }))}
              placeholder="Additional details about this deadline..."
              rows={3}
              className="w-full rounded-xl border border-slate-200 px-4 py-3 text-sm focus:outline-none focus:border-emerald-600 transition-colors resize-none"
            />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1.5">
                Due Date <span className="text-red-500">*</span>
              </label>
              <input
                type="date"
                value={newDeadline.dueDate}
                onChange={(e) => setNewDeadline((p) => ({ ...p, dueDate: e.target.value }))}
                className="w-full rounded-xl border border-slate-200 px-4 py-3 text-sm focus:outline-none focus:border-emerald-600 transition-colors"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1.5">Priority</label>
              <select
                value={newDeadline.priority}
                onChange={(e) => setNewDeadline((p) => ({ ...p, priority: e.target.value }))}
                className="w-full rounded-xl border border-slate-200 px-4 py-3 text-sm focus:outline-none focus:border-emerald-600 transition-colors"
              >
                <option value="critical">Critical</option>
                <option value="high">High</option>
                <option value="medium">Medium</option>
                <option value="low">Low</option>
              </select>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1.5">Jurisdiction</label>
              <select
                value={newDeadline.jurisdiction}
                onChange={(e) => setNewDeadline((p) => ({ ...p, jurisdiction: e.target.value }))}
                className="w-full rounded-xl border border-slate-200 px-4 py-3 text-sm focus:outline-none focus:border-emerald-600 transition-colors"
              >
                <option value="">Select...</option>
                <option value="CA">Canada</option>
                <option value="US">United States</option>
                <option value="PCT">PCT (International)</option>
                <option value="EP">European Patent</option>
                <option value="GB">United Kingdom</option>
                <option value="AU">Australia</option>
                <option value="JP">Japan</option>
                <option value="KR">South Korea</option>
                <option value="CN">China</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1.5">IP Type</label>
              <select
                value={newDeadline.ipType}
                onChange={(e) => setNewDeadline((p) => ({ ...p, ipType: e.target.value }))}
                className="w-full rounded-xl border border-slate-200 px-4 py-3 text-sm focus:outline-none focus:border-emerald-600 transition-colors"
              >
                <option value="">Select...</option>
                <option value="patent">Patent</option>
                <option value="trademark">Trademark</option>
                <option value="copyright">Copyright</option>
                <option value="industrial_design">Industrial Design</option>
                <option value="trade_secret">Trade Secret</option>
              </select>
            </div>
          </div>
        </div>
      </PremiumModal>

      {/* ─── Rules Settings Modal ─────────────────────────────────────────── */}
      <IPRulesSettings
        open={rulesSettingsOpen}
        onClose={() => setRulesSettingsOpen(false)}
      />
    </div>
  );
};

export default IPDocketPage;
